import { ComponentProps } from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../../Typography';
import { SvgBooster } from '../../svgs/Booster';

export type LeaderBoardCellVariant = 'default' | 'myScore' | 'winner';

export type LeaderboardType = 'per_gameweek' | 'overall';
export type PageType = 'readonly';

export interface LeaderBoardCellProps extends ComponentProps<'div'> {
  variant?: LeaderBoardCellVariant;
  rank?: string | number;
  teamId?: string;
  teamName?: string;
  managerName?: string;
  username?: string;
  average?: string | number;
  gameweekPoints?: string | number;
  gameweekId?: string;
  total?: string | number;
  /** When true, shows a booster icon indicating the player used a booster this GW. */
  booster?: boolean;
  showAverage?: boolean;
  showGameweekPoints?: boolean;
  leaderboardType?: LeaderboardType;
  pageType?: PageType;
  onCellClick?: (params: {
    teamId?: string;
    gameweekId?: string;
    leaderboardType?: LeaderboardType;
    pageType?: PageType;
  }) => void;
}

const LeaderBoardCellCVA = cva(
  [
    'flex items-center border-b border-solid px-[--padding-xs] py-[--padding-sm]',
    'min-h-[52px] gap-[--padding-xs]',
    'md:min-h-[62px] md:gap-[--padding-md]',
  ],
  {
    variants: {
      variant: {
        default: 'border-[#eae9fd] bg-[white]',
        myScore: 'border-y border-[#030033] bg-[#201d4a]',
        winner: 'border-y border-[#19b95e] bg-[#8ce28b]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const LeaderBoardCellTextColorMap: Record<LeaderBoardCellVariant, string> = {
  default: 'text-[#222526]',
  myScore: 'text-[white]',
  winner: 'text-[#222526]',
};

export const LeaderBoardCell = ({
  className,
  variant = 'default',
  rank = variant === 'myScore' ? '300000' : '1',
  teamId,
  teamName = 'Team Name Here',
  managerName = '-',
  username = '{Username}',
  average = '128.9',
  gameweekPoints = variant === 'default' ? '120' : '93',
  gameweekId,
  total = '2838',
  booster,
  showAverage = true,
  showGameweekPoints = true,
  leaderboardType,
  pageType,
  onCellClick,
  ...otherProps
}: LeaderBoardCellProps) => {
  const textClassName = LeaderBoardCellTextColorMap[variant];

  const handleClick = () => {
    onCellClick?.({
      teamId,
      gameweekId,
      leaderboardType,
      pageType,
    });
  };

  return (
    <div
      className={twMerge(
        LeaderBoardCellCVA({ variant }),
        className,
        onCellClick ? 'cursor-pointer' : 'cursor-default'
      )}
      onClick={handleClick}
      {...otherProps}
    >
      <div className="flex min-w-0 flex-1 items-center gap-[--padding-xs]">
        <Typography
          as="span"
          variant="subheadline010"
          className={twMerge(
            'w-[50px] shrink-0 text-center md:w-[72px] md:text-14 md:leading-20',
            textClassName
          )}
        >
          {rank}
        </Typography>
        <div className="flex min-w-0 flex-1 items-center gap-[--padding-xs]">
          <div className="grid gap-[--padding-3xs]">
            <Typography
              as="span"
              variant="subheadline010"
              className={twMerge('w-full md:text-14 md:leading-20', textClassName)}
            >
              {teamName}
            </Typography>
            {managerName && (
              <Typography
                as="span"
                variant="paragraph000"
                className={twMerge('w-full md:text-12 md:leading-16', textClassName)}
              >
                {managerName}
              </Typography>
            )}
          </div>
          {booster && (
            <SvgBooster role="img" aria-label="Booster used" className="ml-auto shrink-0" />
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-[--padding-xs] text-center">
        {showAverage && (
          <Typography
            as="span"
            variant="paragraph010"
            className={twMerge('w-[46px] md:w-[72px] md:text-14 md:leading-20', textClassName)}
          >
            {average}
          </Typography>
        )}
        {showGameweekPoints && (
          <Typography
            as="span"
            variant="paragraph010"
            className={twMerge('w-[46px] md:w-[72px] md:text-14 md:leading-20', textClassName)}
          >
            {gameweekPoints}
          </Typography>
        )}
        <Typography
          as="span"
          variant="subheadline010"
          className={twMerge('w-[56px] md:w-[72px] md:text-14 md:leading-20', textClassName)}
        >
          {total}
        </Typography>
      </div>
    </div>
  );
};
